import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// Cache implementation for user data
const userCache = {
  // Structure to store cached results
  cache: {},
  
  // Default expiration time in milliseconds (60 seconds)
  defaultExpiration: 60 * 1000,
  
  // Get a cached result if available and not expired
  get: function(key) {
    const item = this.cache[key];
    if (!item) {
      return null;
    }
    
    // Check if cached item has expired
    if (Date.now() > item.expiry) {
      delete this.cache[key];
      return null;
    }
    
    return item.data;
  },
  
  // Store a result in cache with custom expiration
  set: function(key, data, expirationMs) {
    const expiry = Date.now() + (expirationMs || this.defaultExpiration);
    this.cache[key] = { data, expiry };
  },
  
  // Clear a specific cache entry
  clear: function(key) {
    delete this.cache[key];
  },
  
  // Clear all cache entries
  clearAll: function() {
    this.cache = {};
  },
  
  // Clear all cache entries matching a key prefix
  clearByPrefix: function(prefix) {
    for (const key in this.cache) {
      if (key.startsWith(prefix)) {
        delete this.cache[key];
      }
    }
  }
};

const usersService = {
  // Generate cache keys for different types of requests
  getCacheKey: function(type, params) {
    const paramsStr = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    return `${type}:${paramsStr}`;
  },
  
  // Clear cache when user data might have changed
  invalidateUserCache: function(username = null) {
    if (username) {
      userCache.clearByPrefix(`profile:${username}`);
      userCache.clearByPrefix(`messages:${username}`);
    } else {
      userCache.clearAll();
    }
  },
  
  // Clear cache for a specific username's messages
  invalidateUserMessages: function(username) {
    if (username) {
      userCache.clearByPrefix(`messages:${username}`);
    }
  },
  
  // Clear cache for a specific username's profile
  invalidateUserProfile: function(username) {
    if (username) {
      userCache.clearByPrefix(`profile:${username}`);
    }
  },
  
  // Called when a message is created to invalidate relevant caches
  handleMessageCreated: function(userId, username) {
    // Invalidate the user's profile and messages cache since counts will change
    if (username) {
      usersService.invalidateUserCache(username);
    }
    
    // Also try to invalidate global message cache
    try {
      import('./messages').then(module => {
        if (module && module.default && module.default.invalidateMessageCache) {
          module.default.invalidateMessageCache();
        }
      }).catch(err => {
        console.warn('Could not invalidate message cache:', err);
      });
    } catch (err) {
      console.warn('Could not import messages service:', err);
    }
  },
  
  // Called when a message receives a like to invalidate relevant caches
  handleMessageLiked: function(messageId, userId, username) {
    // Invalidate the message owner's profile
    if (username) {
      usersService.invalidateUserProfile(username);
    }
  },
  
  // Called when a message receives a comment to invalidate relevant caches
  handleMessageCommented: function(messageId, userId, username) {
    // Invalidate the message owner's profile
    if (username) {
      usersService.invalidateUserProfile(username);
    }
    
    // Also try to invalidate comments cache
    try {
      import('./comments').then(module => {
        if (module && module.default && module.default.invalidateMessageComments) {
          module.default.invalidateMessageComments(messageId);
        }
      }).catch(err => {
        console.warn('Could not invalidate comments cache:', err);
      });
    } catch (err) {
      console.warn('Could not import comments service:', err);
    }
  },

  getUserProfile: async (username) => {
    try {
      // Generate cache key
      const cacheKey = `profile:${username}`;
      
      // Check cache first
      const cachedData = userCache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }
      
      const response = await axios.get(`${API_URL}/api/users/${username}/profile`);
      
      // Cache the result for 2 minutes
      userCache.set(cacheKey, response.data, 2 * 60 * 1000);
      
      return response.data;
    } catch (error) {
      console.error('Error al obtener perfil de usuario:', error);
      throw error;
    }
  },
  
  getUserMessages: async (username, page = 1, limit = 10) => {
    try {
      // Generate cache key
      const cacheKey = usersService.getCacheKey(`messages:${username}`, { page, limit });
      
      // Check cache first
      const cachedData = userCache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }
      
      const response = await axios.get(`${API_URL}/api/users/${username}/messages`, {
        params: { page, limit }
      });
      
      // Cache the result - shorter time for first page (more frequently updated)
      const cacheTime = page === 1 ? 60 * 1000 : 2 * 60 * 1000;
      userCache.set(cacheKey, response.data, cacheTime);
      
      return response.data;
    } catch (error) {
      console.error('Error al obtener mensajes del usuario:', error);
      throw error;
    }
  },
  
  deleteMessage: async (messageId) => {
    try {
      // Obtener el token de autenticación
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Configurar los headers
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };
      
      const response = await axios.delete(`${API_URL}/api/messages/${messageId}`, config);
      
      // Get the current user to invalidate their cache
      try {
        const userString = localStorage.getItem('user');
        if (userString) {
          const user = JSON.parse(userString);
          if (user && user.username) {
            // Invalidate cache for this user
            usersService.invalidateUserCache(user.username);
          }
        }
      } catch (err) {
        console.warn('Could not get user from local storage:', err);
      }
      
      // Invalidate message cache
      try {
        const { default: messagesService } = await import('./messages');
        messagesService.invalidateMessageCache();
      } catch (err) {
        console.warn('Could not invalidate message cache:', err);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al eliminar mensaje:', error);
      throw error;
    }
  }
};

export default usersService;